/**
 * Envio de item do catálogo (imagens + texto) pelo Inbox.
 *
 * Causa raiz do bug "spinner/Erro ao enviar item do catálogo": o backend
 * (/api/messages/send[-media] -> safeProviderFetch -> claimSend) EXIGE um
 * `operationId` por envio; sem ele responde 409 SAFE_SEND_CONTEXT_REQUIRED.
 * O CatalogPicker não enviava `operationId`, então toda imagem/texto falhava.
 * Cada parte (imagem i, texto) precisa de um operationId próprio e estável.
 *
 * v2: imagens vão SEM legenda (título/preço ficam só no texto final, sem
 * duplicar) e sem espera artificial entre partes — o backend já serializa.
 */
export interface CatalogSendPost {
  (path: string, body: Record<string, unknown>): Promise<Response>;
}

export interface CatalogSendResult {
  total: number;
  imagesSent: number;
  imagesTotal: number;
  textSent: boolean;
  errors: string[];
}

export async function readSendError(res: Response): Promise<string> {
  let detail = '';
  try {
    const data: any = await res.json();
    detail = data?.error || data?.message || data?.code || '';
    if (!detail && data?.outcome) detail = `envio não confirmado (${data.outcome})`;
  } catch {
    /* corpo não-JSON */
  }
  return detail ? `${detail} (HTTP ${res.status})` : `HTTP ${res.status}`;
}

export async function sendCatalogParts(opts: {
  leadId: string;
  nome: string;
  images: string[];
  text: string;
  post: CatalogSendPost;
  batchId: string;
  delayMs?: number;
  onProgress?: (done: number, total: number) => void;
}): Promise<CatalogSendResult> {
  const { leadId, images, post, batchId, delayMs = 0 } = opts;
  const result: CatalogSendResult = {
    total: images.length + 1,
    imagesSent: 0,
    imagesTotal: images.length,
    textSent: false,
    errors: [],
  };

  const run = async (label: string, path: string, body: Record<string, unknown>) => {
    try {
      const res = await post(path, body);
      if (res.ok) return true;
      result.errors.push(`${label}: ${await readSendError(res)}`);
    } catch (e: any) {
      result.errors.push(`${label}: ${e?.message || 'falha de conexão'}`);
    }
    return false;
  };

  for (let i = 0; i < images.length; i++) {
    const ok = await run(`imagem ${i + 1}/${images.length}`, '/api/messages/send-media', {
      leadId,
      mediaUrl: images[i],
      message_type: 'image',
      content: '',
      operationId: `${batchId}:image:${i}`,
    });
    if (ok) result.imagesSent++;
    opts.onProgress?.(i + 1, result.total);
    if (i < images.length - 1 && delayMs > 0) await new Promise((r) => setTimeout(r, delayMs));
  }

  result.textSent = await run('texto', '/api/messages/send', {
    leadId,
    content: opts.text,
    message_type: 'text',
    operationId: `${batchId}:text`,
  });
  opts.onProgress?.(result.total, result.total);
  return result;
}
